'use client';

import { getCookie } from 'cookies-next/client';
import Script from 'next/script';
import { useEffect, useRef } from 'react';

import { oneTrustConfig } from '../lib/consent-banner-data.ts';
import {
  deriveGcmState,
  enforceConsent,
  isNonUsGeolocation,
} from '../lib/consent-engine.ts';
import { gtag } from '../lib/consent-gtag.ts';

/**
 * Handle consent changes from the SDK (Preference Center saves).
 */
const handleConsentChange = (): void => {
  const optanonConsent = getCookie('OptanonConsent');
  enforceConsent(optanonConsent);
};

/**
 * OneTrust SDK loader.
 *
 * With custom banner: hides SDK banner, listens for Preference Center changes.
 * Without custom banner (standalone): also sets default GCM and enforces on load.
 */
export const OneTrustScripts: React.FC<{
  testMode?: boolean;
  hasBannerComponent?: boolean;
}> = ({ testMode = false, hasBannerComponent = false }) => {
  const consentListenerRef = useRef(false);

  // Standalone mode: set GCM defaults
  useEffect(() => {
    if (!hasBannerComponent) {
      globalThis.dataLayer = globalThis.dataLayer || [];
      const optanonConsent = getCookie('OptanonConsent');
      const hasInteracted = Boolean(getCookie('OptanonAlertBoxClosed'));
      const nonUsGeo = isNonUsGeolocation(
        optanonConsent,
        globalThis.location?.search,
      );
      gtag(
        'consent',
        'default',
        deriveGcmState(optanonConsent, hasInteracted, nonUsGeo),
      );
      if (hasInteracted) {
        enforceConsent(optanonConsent);
      }
    }
  }, [hasBannerComponent]);

  // With custom banner: enforce consent immediately for returning users.
  // Middleware may re-set cookies before the SDK loads (lazyOnload), so we
  // delete opted-out cookies as early as possible on mount.
  useEffect(() => {
    if (hasBannerComponent) {
      const optanonConsent = getCookie('OptanonConsent');
      const hasInteracted = Boolean(getCookie('OptanonAlertBoxClosed'));
      if (hasInteracted) {
        enforceConsent(optanonConsent);
      }
    }
  }, [hasBannerComponent]);

  // OptanonWrapper: SDK callback after init and each consent change.
  // This is the only reliable hook point where the SDK is guaranteed ready.
  useEffect(() => {
    globalThis.OptanonWrapper = () => {
      // Listen for Preference Center consent changes (user saves preferences).
      // Only attach once — subsequent OptanonWrapper calls reuse the listener.
      if (
        !consentListenerRef.current &&
        globalThis.OneTrust?.OnConsentChanged
      ) {
        consentListenerRef.current = true;
        globalThis.OneTrust.OnConsentChanged(() => {
          handleConsentChange();
        });
      }

      // Enforce current consent state (reads OptanonConsent cookie).
      // Safe to call repeatedly — it's idempotent.
      handleConsentChange();
    };

    return () => {
      globalThis.OptanonWrapper = () => {};
    };
  }, [hasBannerComponent]);

  return (
    <>
      {hasBannerComponent && (
        <style>{`#onetrust-banner-sdk { display: none !important; } #onetrust-consent-sdk { pointer-events: none !important; } #onetrust-pc-sdk, #onetrust-pc-dark-filter { pointer-events: auto !important; }`}</style>
      )}
      <Script
        strategy="lazyOnload"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-domain-script={`${oneTrustConfig.domainScriptId}${testMode ? '-test' : ''}`}
      />
    </>
  );
};
